import { createContext, lazy, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { hsl, parseToHsl } from 'polished';
import { useConfig } from '../../hooks/useConfig';
import { usePreviousValue } from '../../hooks/usePreviousValue';
import { useLanguage } from '../../locale/useTextLocalizer';
import { defaultDesignTokens, useDesignTokens } from '../../theme';
import fallbackGlobalSettingsDataProvider from './__internal__/FallbackGlobalSettingsDP';
// imports for SettingsServiceInfoContext
import { getGlobalSettingsDataProvider } from './__internal__/GlobalSettingsDataProvider';
import { fireSettingsChange, useSubscribeForSettingsChange } from './events/SettingsChangeEvent';
import { GlobalSettingsKeys, STARTUP_SCREEN_ACTION_VALUES, UI5_NO_COLOR_VALUE } from './GlobalSettingsKeys';
import { useApplyGlobalSettingsChange } from './hooks/useApplyGlobalSettingsChange';
import { useAppSwitcherPreferenceItemsMessaging } from './hooks/useAppSwitcherPreferenceItemsMessaging';
import { useDeleteSettings } from './hooks/useDeleteSettings';
import { useGetSettings } from './hooks/useGetSettings';
import { useHasSettingsWriteAccess } from './hooks/useHasSettingsWriteAccess';
import { useListenInvertAppColorChange } from './hooks/useListenInvertAppColorChange';
import { useSetSettings } from './hooks/useSetSettings';
import { TransientPreferenceService } from './remoteProxies/TransientPreferenceService';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const InvertAppColorErrorDialog = /*#__PURE__*/lazy(() => import('./controls/InvertAppColorErrorDialog'));
function getGlobalSettingsKnownDefaults() {
  return {
    [GlobalSettingsKeys.THEMES_KEY]: 'sas_corporate',
    [GlobalSettingsKeys.PLAY_EARCONS]: 'false',
    [GlobalSettingsKeys.PLAY_VISUAL_EFFECTS]: 'true',
    [GlobalSettingsKeys.ENABLE_POPUP_DISPLAY_DURATION]: 'false',
    [GlobalSettingsKeys.POPUP_DISPLAY_DURATION]: '5',
    [GlobalSettingsKeys.FOCUS_COLOR]: defaultDesignTokens.tokensExperimental['color-action-background-focus'],
    [GlobalSettingsKeys.FOCUS_THICKNESS]: defaultDesignTokens.tokensExperimental['size-action-border-width-focus'],
    [GlobalSettingsKeys.FOCUS_OPACITY]: '100',
    [GlobalSettingsKeys.FOCUS_OVERRIDE]: 'false',
    [GlobalSettingsKeys.INVERT_APP_COLORS]: 'false',
    [GlobalSettingsKeys.FOCUS_TRUNCATED_HEADINGS]: 'false',
    [GlobalSettingsKeys.STARTUP_SCREEN_ACTION]: STARTUP_SCREEN_ACTION_VALUES.LAST_APP_DIRECTIVE
  };
}

// For the User Suppressed Messages, there is a requirement to also mutate the setting from MessageDialog,
// hence have provided an exclusive hook below.

// Offline locale is the only settings Where we don't know what the default value could be
// hence would be returned as undefined.

export function useGetGlobalSetting(key) {
  const service = useGlobalSettingsService();
  const [value, setValue] = useState(() => service.get(key));
  useEffect(() => {
    function change(value) {
      setValue(value);
    }
    service.registerChangeListener(key, change);
    return () => {
      service.deRegisterChangeListener(key, change);
    };
  }, [service, key]);
  return value;
}
export function useSuppressMessagesGlobalSetting() {
  const service = useGlobalSettingsService();
  const key = GlobalSettingsKeys.USER_SUPPRESSED_MESSAGES;
  const [suppressedMessages, setSuppressedMessages] = useState(() => service.get(key)?.split('|') || []);
  useEffect(() => {
    function change(value) {
      setSuppressedMessages(value?.split('|') || []);
    }
    service.registerChangeListener(key, change);
    return () => {
      service.deRegisterChangeListener(key, change);
    };
  }, [service, key]);
  const updateSuppressedMessages = useCallback(value => {
    if (value.length > 0) {
      service.set(GlobalSettingsKeys.USER_SUPPRESSED_MESSAGES, value.join('|'));
    } else {
      service.reset(GlobalSettingsKeys.USER_SUPPRESSED_MESSAGES);
    }
  }, [service]);
  return {
    suppressedMessages,
    updateSuppressedMessages
  };
}
const globalSettingsServiceError = () => {
  console.error('The global settings service must be used within GlobalSettingsServiceContext Provider');
};
let globalSettingsService = {
  get: key => getGlobalSettingsKnownDefaults()[key],
  set: () => globalSettingsServiceError(),
  reset: () => globalSettingsServiceError(),
  // marking these props as empty functions to prevent unnecessary unit test failures
  registerChangeListener: () => {},
  deRegisterChangeListener: () => {}
};

// Provided a default value so that any tests involving component/s using
// useGetGlobalSetting hook do not break
const GlobalSettingsDispatchContext = /*#__PURE__*/createContext(globalSettingsService);
function useGlobalSettingsService() {
  return useContext(GlobalSettingsDispatchContext);
}
let listeners = {};
let listenerCount = 0;
let globalSettingsServiceSet = false;

/**
 * Registers callback that will run when the GlobalSettingsDispatch object changes.
 *
 * Note: `useGetGlobalSetting` hook is the desired way to get settings in a component.
 * This function is only for cases without GlobalSettingsDispatchContext.
 *
 * @param A callback function to for when the GlobalSettingsDispatch object changes.
 * @returns A function that will deregister the provided callback.
 */
export const addOnGlobalSettingsServiceUpdate = listener => {
  if (globalSettingsServiceSet) {
    listener(globalSettingsService);
  }
  const id = listenerCount;
  listeners[id] = listener;
  listenerCount++;
  return () => {
    const {
      [id]: _,
      ...rest
    } = listeners;
    listeners = rest;
  };
};
const onGlobalSettingsServiceUpdate = service => {
  // Object.values was giving a compile error in VSCode.
  Object.keys(listeners).forEach(k => listeners[k](service));
};

/**
 * Note: `useGetGlobalSetting` hook is the desired way to get settings in a component.
 * This function is only for cases without GlobalSettingsDispatchContext.
 */
export const getGlobalSettingsService = () => globalSettingsService;
function GlobalSettingsApplication(props) {
  const appId = useConfig()?.get('appId');
  const [itemsToSave, setItemsToSave] = useState([]);
  const [keysToDelete, setKeysToDelete] = useState([]);
  // special treatment for only invertAppColor setting mutation using keyboard.
  const [invertAppColorSaveError, setInvertAppColorSaveError] = useState(false);

  // denotes the persisted setting values or their defaults.
  const globalSettingsRef = useRef({});
  const changeListenersRef = useRef(new Map());
  const [persistedGlobalSettings, setPersistedGlobalSettings] = useState({});

  // themes, focusColor, focusThickness, formatLocale are a few global settings
  // whose default values needs to be derived.
  const dt = useDesignTokens();
  const focusToken = {
    bd: dt.tokens['color-action-border-focus'],
    bdWidth: '2px',
    bdStyle: dt.tokens['style-action-border-focus']
  };
  const focusColor = persistedGlobalSettings[GlobalSettingsKeys.FOCUS_COLOR] ?? hsl(parseToHsl(focusToken.bd));
  const focusThickness = persistedGlobalSettings[GlobalSettingsKeys.FOCUS_THICKNESS] ?? focusToken.bdWidth.replace(/px$/, '');
  const defaultLanguage = useLanguage();
  const formatLocale = persistedGlobalSettings[GlobalSettingsKeys.FORMAT_LOCALE_KEY] ?? defaultLanguage;
  const [defaultTheme, setDefaultTheme] = useState(undefined);
  const getDefaultAppTheme = props.getDefaulApplicationTheme;
  const updateDefaultTheme = useCallback(() => {
    getDefaultAppTheme().then(data => setDefaultTheme(data.id), () => {
      console.error('Error fetching default theme from themes service.');
    });
  }, [getDefaultAppTheme]);
  const knownDefaults = getGlobalSettingsKnownDefaults();
  const defaultValues = {
    ...knownDefaults,
    [GlobalSettingsKeys.THEMES_KEY]: defaultTheme ?? knownDefaults[GlobalSettingsKeys.THEMES_KEY],
    [GlobalSettingsKeys.FORMAT_LOCALE_KEY]: formatLocale,
    [GlobalSettingsKeys.FOCUS_COLOR]: focusColor,
    [GlobalSettingsKeys.FOCUS_THICKNESS]: focusThickness
  };
  globalSettingsRef.current = {
    ...defaultValues,
    ...persistedGlobalSettings
  };
  useApplyGlobalSettingsChange({
    ...globalSettingsRef.current,
    [GlobalSettingsKeys.FOCUS_COLOR]: persistedGlobalSettings[GlobalSettingsKeys.FOCUS_COLOR],
    [GlobalSettingsKeys.FOCUS_THICKNESS]: persistedGlobalSettings[GlobalSettingsKeys.FOCUS_THICKNESS],
    [GlobalSettingsKeys.FOCUS_OPACITY]: persistedGlobalSettings[GlobalSettingsKeys.FOCUS_OPACITY]
  });
  useInvokeChangeListeners(changeListenersRef.current, globalSettingsRef.current);
  const changeListener = useCallback((_, data) => {
    const event = data?.data;
    if (event) {
      const globalSettingsIds = Object.keys(GlobalSettingsKeys).map(key => GlobalSettingsKeys[key]);
      const changes = {};
      for (const change of event.data) {
        if (globalSettingsIds.indexOf(change.key) >= 0) {
          // UI5 compatibility
          switch (change.key) {
            case GlobalSettingsKeys.THEMES_KEY:
              // eslint-disable-next-line @sas/i18n/jsx-to-localize
              change.value = change.value === 'Default' ? undefined : change.value;
              if (!change.value) {
                updateDefaultTheme();
              }
              break;
            case GlobalSettingsKeys.FORMAT_LOCALE_KEY:
            case GlobalSettingsKeys.OFFLINE_LOCALE_KEY:
              // eslint-disable-next-line @sas/i18n/jsx-to-localize
              change.value = change.value === 'Default' ? undefined : change.value;
              break;
            case GlobalSettingsKeys.FOCUS_COLOR:
              change.value = change.value === UI5_NO_COLOR_VALUE ? undefined : change.value;
          }
          changes[change.key] = change.value;
        }
      }
      setPersistedGlobalSettings(settings => {
        const updated = {
          ...settings,
          ...changes
        };
        return Object.keys(updated).reduce((result, key) => updated[key] !== undefined ? {
          ...result,
          [key]: updated[key]
        } : result, {});
      });
    }
  }, [updateDefaultTheme]);

  // settings messaging
  useSubscribeForSettingsChange(changeListener);

  // App switcher messaging
  useAppSwitcherPreferenceItemsMessaging(appId);
  const resolveGetSettings = useCallback(items => {
    const globalSettingsIds = Object.keys(GlobalSettingsKeys).map(key => GlobalSettingsKeys[key]);
    const globalSettings = items.reduce((globalSettings, item) => {
      if (globalSettingsIds.indexOf(item.id) >= 0) {
        return {
          ...globalSettings,
          [item.id]: item.value
        };
      }
      return globalSettings;
    }, {});
    if (!globalSettings[GlobalSettingsKeys.THEMES_KEY]) {
      updateDefaultTheme();
    }
    if (globalSettings[GlobalSettingsKeys.FOCUS_COLOR] === UI5_NO_COLOR_VALUE) {
      delete globalSettings[GlobalSettingsKeys.FOCUS_COLOR];
    }
    setPersistedGlobalSettings(settings => ({
      ...settings,
      ...globalSettings
    }));
  }, [updateDefaultTheme]);
  const rejectGetSettings = useCallback(() => {
    console.error('Get preferences call to obtain global settings failed.');
  }, []);
  useGetSettings(resolveGetSettings, rejectGetSettings);
  const resolveSetSettings = useCallback(items => {
    setItemsToSave([]);
    const eventChange = items.reduce((change, item) => {
      return change.concat([{
        key: item.id,
        value: item.value || undefined,
        oldValue: globalSettingsRef.current[item.id]
      }]);
    }, []);
    fireSettingsChange(eventChange, appId);
  }, [appId]);
  const rejectSetSettings = useCallback(() => {
    setItemsToSave([]);
    const ids = itemsToSave.map(item => item.id);
    console.error(`Failed to save the following global settings ${ids}`);
    const hasInvertAppColorSetting = ids.indexOf(GlobalSettingsKeys.INVERT_APP_COLORS) >= 0;
    if (hasInvertAppColorSetting) {
      setInvertAppColorSaveError(true);
    }
  }, [itemsToSave]);
  useSetSettings(itemsToSave, resolveSetSettings, rejectSetSettings);
  const resolveDeleteSettings = useCallback(() => {
    setKeysToDelete([]);
    const items = keysToDelete.map(key => ({
      id: key,
      value: ''
    }));
    const eventChange = items.reduce((change, item) => {
      return change.concat([{
        key: item.id,
        value: item.value || undefined,
        oldValue: globalSettingsRef.current[item.id]
      }]);
    }, []);
    fireSettingsChange(eventChange, appId);
  }, [keysToDelete, appId]);
  const rejectDeleteSettings = useCallback(() => {
    console.error(`Failed to save the following global settings ${keysToDelete}`);
    const hasInvertAppColorSetting = keysToDelete.indexOf(GlobalSettingsKeys.INVERT_APP_COLORS) >= 0;
    if (hasInvertAppColorSetting) {
      setInvertAppColorSaveError(true);
    }
    setKeysToDelete([]);
  }, [keysToDelete]);
  useDeleteSettings(keysToDelete, resolveDeleteSettings, rejectDeleteSettings);
  const invertAppColorChangeHandler = useCallback(() => {
    const existingValue = Boolean(persistedGlobalSettings[GlobalSettingsKeys.INVERT_APP_COLORS]);
    if (existingValue) {
      setKeysToDelete(keys => keys.concat(GlobalSettingsKeys.INVERT_APP_COLORS));
      setItemsToSave(items => items.filter(item => item.id !== GlobalSettingsKeys.INVERT_APP_COLORS));
    } else {
      setKeysToDelete(keys => keys.filter(key => key !== GlobalSettingsKeys.INVERT_APP_COLORS));
      setItemsToSave(items => items.concat({
        id: GlobalSettingsKeys.INVERT_APP_COLORS,
        value: 'true'
      }));
    }
  }, [persistedGlobalSettings]);
  useListenInvertAppColorChange(invertAppColorChangeHandler);
  const service = useMemo(() => {
    const result = {
      get: key => globalSettingsRef.current[key],
      set: (key, value) => setItemsToSave(items => items.concat({
        id: key,
        value
      })),
      reset: key => setKeysToDelete(keys => keys.concat(key)),
      registerChangeListener: (key, handle) => {
        const callbacks = changeListenersRef.current.get(key) || [];
        changeListenersRef.current.set(key, [...callbacks, handle]);
      },
      deRegisterChangeListener: (key, handle) => {
        const callbacks = changeListenersRef.current.get(key);
        if (Array.isArray(callbacks)) {
          changeListenersRef.current.set(key, callbacks.filter(callback => callback !== handle));
        }
      }
    };
    globalSettingsService = result;
    globalSettingsServiceSet = true;
    onGlobalSettingsServiceUpdate(result);
    return result;
  }, []);
  return /*#__PURE__*/_jsxs(GlobalSettingsDispatchContext.Provider, {
    value: service,
    children: [invertAppColorSaveError ? /*#__PURE__*/_jsx(InvertAppColorErrorDialog, {
      onClose: () => setInvertAppColorSaveError(false)
    }) : null, props.children]
  });
}
function useInvokeChangeListeners(listeners, currentValues) {
  const previousValues = usePreviousValue(currentValues);
  useEffect(() => {
    if (previousValues !== undefined) {
      for (const key in GlobalSettingsKeys) {
        const id = GlobalSettingsKeys[key];
        const value = currentValues[id];
        const previousValue = previousValues[id];
        if (value !== previousValue) {
          const aListeners = listeners.get(id);
          aListeners?.forEach(listener => listener.apply(null, [value]));
        }
      }
    }
  }, [listeners, currentValues, previousValues]);
}

// SettingsServiceInfo Context

const SettingsServiceContext = /*#__PURE__*/createContext({
  writeAccessState: {
    isWriteAccessDetected: true,
    hasWriteAccess: true
  },
  dataProvider: fallbackGlobalSettingsDataProvider
});
export function useSettingsService() {
  return useContext(SettingsServiceContext);
}
export function GlobalSettingsProvider(props) {
  const dataProvider = props.dataProvider ?? getGlobalSettingsDataProvider();
  const {
    isWriteAccessDetectionPending,
    hasWriteAccess
  } = useHasSettingsWriteAccess(dataProvider); // this hook is backed by a state

  const _preferenceService = useMemo(() => {
    let serviceHandle = dataProvider;
    if (!isWriteAccessDetectionPending && !hasWriteAccess) {
      serviceHandle = new TransientPreferenceService();
    }
    return {
      writeAccessState: {
        isWriteAccessDetected: !isWriteAccessDetectionPending,
        hasWriteAccess
      },
      dataProvider: serviceHandle
    };
  }, [dataProvider, isWriteAccessDetectionPending, hasWriteAccess]);
  return /*#__PURE__*/_jsx(SettingsServiceContext.Provider, {
    value: _preferenceService,
    children: /*#__PURE__*/_jsx(GlobalSettingsApplication, {
      getDefaulApplicationTheme: dataProvider?.getTheme,
      children: props.children
    })
  });
}
//# sourceMappingURL=GlobalSettingsProvider.js.map